import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions } from '@material-ui/core';

import { useForm, FormProvider } from 'react-hook-form';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';
import { showMessage } from 'app/store/fuse/messageSlice';

import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';

const TYPE_DISTURBANCE = 'Disturbance Variable';
const TYPE_SET_POINT = 'Set Point';

const parseNumber = (x) => {
    if (x == null) return null;
    const s = String(x).trim().replace(',', '.');
    if (s === '') return null;
    const v = Number(s);
    return Number.isFinite(v) ? v : NaN;
};

const getRandomInt = (min, max) => {
    const _min = Math.ceil(min);
    const _max = Math.floor(max);
    return Math.floor(Math.random() * (_max - _min + 1)) + _min;
};

const getRandomStepFloat = (min, max, step) => {
    const steps = Math.round((max - min) / step);
    const randomStepIndex = getRandomInt(0, steps);
    const value = min + randomStepIndex * step;
    return Number(value.toFixed(10));
};

const getRandomDynamicCheckValues = () => {
    return {
        HMB_2003: getRandomInt(39, 40),
        HMB_2006: getRandomInt(39, 40),
        'FIC-2001_NEW_SP': getRandomInt(24000, 26000),
        'TIC-2054_NEW_SP': getRandomInt(40, 50),
        'TIC-2053_NEW_SP': getRandomInt(45, 50),
        'FIC-2057_NEW_SP': getRandomInt(50000, 60000),
        'TIC-2052_SP_RATE': getRandomInt(46, 50),
        'FFIC-2060_SP_RATE': getRandomStepFloat(0.2, 0.3, 0.01)
    };
};

const buildRowsFromRandomSource = () => {
    const baseValues = getRandomDynamicCheckValues();
    const bestValues = getRandomDynamicCheckValues();

    return [
        {
            type: TYPE_DISTURBANCE,
            attribute: 'HMB_2003',
            base: baseValues.HMB_2003,
            best: bestValues.HMB_2003
        },
        {
            type: TYPE_DISTURBANCE,
            attribute: 'HMB_2006',
            base: baseValues.HMB_2006,
            best: bestValues.HMB_2006
        },
        {
            type: TYPE_SET_POINT,
            attribute: 'FIC-2001_NEW_SP',
            base: baseValues['FIC-2001_NEW_SP'],
            best: bestValues['FIC-2001_NEW_SP']
        },
        {
            type: TYPE_SET_POINT,
            attribute: 'TIC-2054_NEW_SP',
            base: baseValues['TIC-2054_NEW_SP'],
            best: bestValues['TIC-2054_NEW_SP']
        },
        {
            type: TYPE_SET_POINT,
            attribute: 'TIC-2053_NEW_SP',
            base: baseValues['TIC-2053_NEW_SP'],
            best: bestValues['TIC-2053_NEW_SP']
        },
        {
            type: TYPE_SET_POINT,
            attribute: 'FIC-2057_NEW_SP',
            base: baseValues['FIC-2057_NEW_SP'],
            best: bestValues['FIC-2057_NEW_SP']
        },
        {
            type: TYPE_SET_POINT,
            attribute: 'TIC-2052_SP_RATE',
            base: baseValues['TIC-2052_SP_RATE'],
            best: bestValues['TIC-2052_SP_RATE']
        },
        {
            type: TYPE_SET_POINT,
            attribute: 'FFIC-2060_SP_RATE',
            base: baseValues['FFIC-2060_SP_RATE'],
            best: bestValues['FFIC-2060_SP_RATE']
        }
    ];
};

export default function DynamicValidationSettingsPopup() {
    const { setPopup, ts, setDynamicValidationSettings } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);
    const dispatch = useDispatch();

    const [running, setRunning] = useState(false);

    const methods = useForm({
        defaultValues: { items: [] },
        mode: 'onChange'
    });

    useEffect(() => {
        const rows = buildRowsFromRandomSource();
        methods.reset({ items: rows });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCancel = () => setPopup?.(null);

    const run = async (formValues) => {
        if (!ts) {
            dispatch(showMessage({ message: 'Missing timestamp.' }));
            return;
        }

        const payload = {
            base: {},
            best: {}
        };

        let hasError = false;

        if (Array.isArray(formValues?.items)) {
            formValues.items.forEach((row, idx) => {
                if (!row?.attribute) return;

                const baseValue = parseNumber(row?.base);
                const bestValue = parseNumber(row?.best);

                if (baseValue === null) {
                    methods.setError(`items.${idx}.base`, { type: 'required' });
                    hasError = true;
                    return;
                }

                if (Number.isNaN(baseValue)) {
                    methods.setError(`items.${idx}.base`, { type: 'validate', message: 'Invalid number' });
                    hasError = true;
                    return;
                }

                if (bestValue === null) {
                    methods.setError(`items.${idx}.best`, { type: 'required' });
                    hasError = true;
                    return;
                }

                if (Number.isNaN(bestValue)) {
                    methods.setError(`items.${idx}.best`, { type: 'validate', message: 'Invalid number' });
                    hasError = true;
                    return;
                }

                payload.base[row.attribute] = baseValue;
                payload.best[row.attribute] = bestValue;
            });
        }

        if (hasError) {
            dispatch(showMessage({ message: 'Please fill all required values.' }));
            return;
        }

        if (Object.keys(payload.base).length === 0 || Object.keys(payload.best).length === 0) {
            dispatch(showMessage({ message: 'No values to process.' }));
            return;
        }

        setRunning(true);
        try {
            setDynamicValidationSettings({
                ts,
                ...payload
            });

            console.log('[DynamicValidationSettingsPopup.run] -> set dynamicValidationSettings', {
                ts,
                ...payload
            });

            setPopup?.('DYNAMIC_VALIDATION');
        } catch (err) {
            console.error('[DynamicValidationSettingsPopup] run error:', err);
            dispatch(showMessage({ message: 'Run failed.' }));
        } finally {
            setRunning(false);
        }
    };

    const items = methods.watch('items') || [];
    const itemsLen = items.length;

    const canRun =
        !!ts &&
        !running &&
        itemsLen > 0 &&
        items.every((row) => {
            if (!row?.attribute) return false;

            const baseValue = parseNumber(row?.base);
            const bestValue = parseNumber(row?.best);

            return baseValue !== null && !Number.isNaN(baseValue) && bestValue !== null && !Number.isNaN(bestValue);
        });

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open fullWidth maxWidth='md'>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className='flex items-center justify-between gap-32 px-32'>
                        <div className='whitespace-nowrap'>EDT Dynamic Validation Settings</div>
                    </div>
                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ maxHeight: '520px' }}>
                    {itemsLen === 0 && <div className='text-xs text-center text-gray-500'>No data found for timestamp.</div>}

                    {itemsLen > 0 && (
                        <div className='flex-1 overflow-auto pr-4'>
                            <FormProvider {...methods}>
                                <EditFormGridComponent
                                    config={{ topic: 'model' }}
                                    fieldConfig={{
                                        key: 'items',
                                        component: EditFormGridComponent,
                                        modifyOnly: true,
                                        readOnly: true,
                                        defaultValue: {
                                            type: '',
                                            attribute: '',
                                            base: null,
                                            best: null
                                        },
                                        fields: [
                                            {
                                                key: 'type',
                                                text: 'Type',
                                                component: EditFormTextField,
                                                required: false,
                                                readOnly: true
                                            },
                                            {
                                                key: 'attribute',
                                                text: 'Attribute',
                                                component: EditFormTextField,
                                                required: true,
                                                readOnly: true
                                            },
                                            {
                                                key: 'base',
                                                text: 'Base',
                                                component: EditFormTextField,
                                                required: true,
                                                type: 'number'
                                            },
                                            {
                                                key: 'best',
                                                text: 'Best',
                                                component: EditFormTextField,
                                                required: true,
                                                type: 'number'
                                            }
                                        ]
                                    }}
                                />
                            </FormProvider>
                        </div>
                    )}
                </DialogContent>

                <DialogActions className='justify-between border-t p-20'>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button color='primary' variant='contained' onClick={methods.handleSubmit(run)} autoFocus disabled={!canRun}>
                        {running ? 'Running' : 'Run'}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}